import type { TFunction } from "i18next";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Всплывашка «товар в корзине» с действием.
 *
 * Тост ведёт в корзину («Перейти в корзину») и даёт остаться в каталоге
 * («Продолжить покупки» — просто закрывает уведомление).
 *
 * Раньше это был стандартный success-тост sonner с двумя кнопками action/cancel.
 * В его 356px кнопки забирали почти всю ширину, и заголовок сжимался в столбик
 * по букве (плашка вырастала до ~440px в высоту), а зелёная заливка richColors и
 * синяя кнопка не из палитры. Плюс он садился в top-center поверх шапки и поля
 * поиска. Теперь это своя карточка в палитре витрины: фото товара, заголовок,
 * название, под ними две равные кнопки; на десктопе — справа под иконкой
 * корзины, на телефоне — во всю ширину под шапкой (отступы — у `<Toaster>`).
 *
 * Кнопки — новые, макетные (Айталы 02.10, A-23): DS `Button` M (40, r10,
 * 14/18 w600), тёмная и серая, вместо прежних «пилюль».
 *
 * Повторное добавление не плодит стопку: у тоста постоянный id, он обновляется
 * на месте и заново отсчитывает время.
 */
export type AddedToCartItem = {
  photo?: string;
  brand?: string;
  name?: string;
};
const TOAST_ID = "cart-added";
export const notifyAddedToCart = (t: TFunction, goToCart: () => void, item?: AddedToCartItem, /** Сколько позиций положено разом (кнопка «N товаров» в идее). */
count = 1): void => {
  const many = count > 1;
  const title = many ? t("cart.addedMany") : t("product.added");
  const subtitle = many ? t("cart.addedCount", {
    count
  }) : [item?.brand, item?.name].filter(Boolean).join(" · ");
  toast.custom(id => <div data-testid="cart-added-toast" className="flex w-full flex-col gap-3 rounded-[16px] border border-osmix-gray-300 bg-white p-3 text-osmix-text-1 shadow-[0_12px_30px_rgba(41,55,76,0.16)] sm:w-[360px]">
				<div className="flex items-center gap-3">
					{item?.photo ? <img src={item.photo} alt="" className="h-[60px] w-[48px] shrink-0 rounded-[8px] bg-osmix-gray-100 object-cover" /> : <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-osmix-gray-100">
							<Check className="size-5 text-osmix-tangelo" aria-hidden="true" />
						</span>}
					<div className="min-w-0 flex-1">
						<p className="type-body2-bold">{title}</p>
						{subtitle && <p className="mt-0.5 truncate type-small text-osmix-icon">{subtitle}</p>}
					</div>
					<button type="button" onClick={() => toast.dismiss(id)} aria-label={t("common.close", {
        defaultValue: "Закрыть"
      })} className="-mr-1 flex size-7 shrink-0 items-center justify-center self-start rounded-full text-osmix-icon outline-none transition-colors hover:bg-osmix-gray-100 hover:text-osmix-text-1 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
						<X className="size-4" aria-hidden="true" />
					</button>
				</div>
				{/* Ширина кнопок — по подписи (flex-auto): в 360px две равные M-кнопки
        обрезали «Продолжить покупки» на Manrope 14 w600. */}
				<div className="flex gap-2">
					<Button size="md" onClick={() => {
        toast.dismiss(id);
        goToCart();
      }} className="min-w-0 flex-auto px-2.5">
						<span className="truncate">{t("cart.goToCart")}</span>
					</Button>
					<Button variant="gray" size="md" onClick={() => toast.dismiss(id)} className="min-w-0 flex-auto px-2.5">
						<span className="truncate">{t("cart.keepShopping")}</span>
					</Button>
				</div>
			</div>, {
    id: TOAST_ID,
    position: "top-right",
    duration: 6000
  });
};